import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
    Caching,
    FulfillAction,
    CacheRetrieveFn,
    CacheStoreFn,
    CacheConfigFn,
    RejectAction,
} from '../cache';
import { assert } from '../../Utility/assert';
import { ILog } from '../../AuxillaryService/Log';

enum CacheStatus {
    FromCache = 270,
    MergedWithCache,
}

const CACHE_STATUS_TEXT = new Map([
    [CacheStatus.FromCache, 'OK (Pulled from Cache)'],
    [CacheStatus.MergedWithCache, 'OK (Merged with Cached data)'],
]);

interface ApiInstance {
    api: AxiosInstance;
    cacheConfig: CacheConfigFn;
    log: ILog;
}

let _apiInstance: ApiInstance | undefined;

/**
 * Get an instance of {@link https://axios-http.com/ axios} that will
 * automatically apply the authorization headers needed to access
 * {@link https://reachcm.com/papi/v2}.
 */
export async function getApiInstance(): Promise<ApiInstance> {
    if (_apiInstance === undefined) {
        throw new TypeError('The REACH API has not been configured.');
    }
    return _apiInstance;
}

interface InstanceConfig {
    baseUrl: string;
    /** Player IAM Token or CMS Security Token */
    getToken: () => Promise<string>;
    cacheStore: CacheStoreFn;
    cacheRetrieve: CacheRetrieveFn;

    log: ILog;
}

export function setApiInstance({
    baseUrl,
    getToken,
    cacheStore,
    cacheRetrieve,
    log,
}: InstanceConfig): void {
    if (_apiInstance !== undefined) {
        throw new TypeError('REACH API v2 has already been configured.');
    }

    const cachingInstance = new Caching(cacheStore, cacheRetrieve);

    const axiosOpts: AxiosRequestConfig = {
        baseURL: baseUrl,
    };
    const apiInstance = axios.create(axiosOpts);

    // Authorization
    apiInstance.interceptors.request.use(async (config) => {
        if (!config.headers.has('Authorization')) {
            try {
                const token: string = await getToken();
                config.headers.set('Authorization', `Bearer ${token}`);
            } catch (err) {
                log.Warning(`Reach IAM token retrieval failed: ${err}`, null, __fileline, err);
            }
        }
        return config;
    });

    // Caching
    apiInstance.interceptors.response.use(
        async function (response) {
            // save
            assert(response.config.url !== undefined);

            const params =
                response.config.params instanceof URLSearchParams
                    ? Object.fromEntries(response.config.params.entries())
                    : response.config.params;

            const [newData, actionTaken] = await cachingInstance.interceptFulfilled(
                response.config.url,
                params ?? {},
                response.data
            );

            if (actionTaken === FulfillAction.CachedAndModified) {
                return {
                    ...response,
                    data: newData,
                    status: CacheStatus.MergedWithCache,
                    statusText: CACHE_STATUS_TEXT.get(CacheStatus.MergedWithCache) ?? 'OK',
                };
            }

            return response;
        },
        async (error) => {
            // extract
            if (!axios.isAxiosError(error)) {
                throw error;
            }

            assert(error.config?.url !== undefined);
            const params =
                error.config.params instanceof URLSearchParams
                    ? Object.fromEntries(error.config.params.entries())
                    : error.config.params;

            const [cachedData, status] = await cachingInstance.interceptRejected(
                error.config.url,
                params ?? {}
            );

            if (status !== RejectAction.Retrieved) {
                const actionMap = new Map([
                    [
                        RejectAction.ErrorUnknownUrl,
                        'The given URL is not configured to have cached data.',
                    ],
                    [RejectAction.ErrorNoCachedData, 'No cached data found.'],
                ]);
                log.Debug(
                    `Unable to retrieve cached data: ${actionMap.get(status) ?? 'Unknown Error.'}`,
                    null,
                    __fileline
                );

                throw error;
            }

            return {
                data: cachedData,
                status: CacheStatus.FromCache,
                statusText: CACHE_STATUS_TEXT.get(CacheStatus.FromCache) ?? 'OK',
                headers: {},
                config: error.config,
            };
        }
    );

    _apiInstance = {
        api: apiInstance,
        cacheConfig: (type, url, options) => cachingInstance.add(type, url, options),
        log,
    };
}
